/**
 * Utility functions for the Review Comments view.
 * Requirements: 3.1, 3.3, 4.1, 4.3, 5.1, 5.5
 */

import type { ReviewCommentViewItem, SectionCategory } from './types';

/**
 * Derive a sorted, deduplicated (case-insensitive), max-50 reviewer list
 * from comments filtered by the active category.
 *
 * Requirements: 3.1, 3.3
 */
export function getVisibleReviewers(
  comments: ReviewCommentViewItem[],
  activeCategory: SectionCategory | null
): string[] {
  const filtered = activeCategory
    ? comments.filter((c) => c.sectionCategory === activeCategory)
    : comments;

  const namesSet = new Set<string>();
  const namesLower = new Set<string>();

  for (const c of filtered) {
    const lower = c.commenterName.toLowerCase();
    if (!namesLower.has(lower)) {
      namesLower.add(lower);
      namesSet.add(c.commenterName);
    }
  }

  return [...namesSet]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .slice(0, 50);
}

/**
 * Filter comments by category and reviewer, sorted descending by createdAt.
 *
 * Requirements: 4.1, 5.1, 5.5
 */
export function getFilteredComments(
  comments: ReviewCommentViewItem[],
  activeCategory: SectionCategory | null,
  selectedReviewer: string | 'all'
): ReviewCommentViewItem[] {
  let result = comments;

  if (activeCategory) {
    result = result.filter((c) => c.sectionCategory === activeCategory);
  }

  if (selectedReviewer !== 'all') {
    result = result.filter(
      (c) => c.commenterName.toLowerCase() === selectedReviewer.toLowerCase()
    );
  }

  return result.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/**
 * Format an ISO date string as "MMM D, YYYY h:mm AM/PM".
 * ReviewComment createdAt is a true UTC timestamp, so using new Date() is appropriate.
 *
 * Requirements: 4.3
 */
export function formatCommentDate(isoString: string): string {
  const date = new Date(isoString);

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${datePart} ${timePart}`;
}
